import * as React from "react";
import type { Virtualizer } from "@tanstack/react-virtual";
import { cn } from "@/modern/utils";
import {
  ENTITY_FINDER_GUTTER_X,
  ENTITY_FINDER_SECTION_CLS,
} from "@/modern/components/entity-finder/constants/layout";
import { getConfigEntry } from "@/modern/components/entity-finder/constants/config";
import type {
  EntityFinderContext,
  EntityFinderItem,
  EntityFinderKind,
  EntityFinderListEntry,
} from "@/modern/components/entity-finder/types";
import { EntityFinderSettingRow } from "@/modern/components/entity-finder/components/EntityFinderSettingRow";
import { EntityFinderListItem } from "@/modern/components/entity-finder/components/EntityFinderListItem";

export function EntityFinderSectionHeader({
  title,
  isFirst = false,
  compact = false,
  className,
}: {
  title: string;
  isFirst?: boolean;
  compact?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        ENTITY_FINDER_GUTTER_X,
        "flex items-center gap-3",
        compact ? "pb-1" : "pb-2",
        isFirst ? "pt-3" : "pt-4",
        className
      )}
    >
      <span className={cn("shrink-0", ENTITY_FINDER_SECTION_CLS)}>{title}</span>
      <div className="h-px flex-1 bg-border/60" />
    </div>
  );
}

interface EntityFinderListProps {
  entries: EntityFinderListEntry[];
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  query: string;
  ctx: EntityFinderContext;
  onSelect: (item: EntityFinderItem, configType: EntityFinderKind) => void;
  className?: string;
}

function renderItemContent(
  item: EntityFinderItem,
  configType: EntityFinderKind,
  query: string,
  ctx: EntityFinderContext
) {
  const entry = getConfigEntry(configType);
  if (!entry) return null;

  const metadata = entry.getMetadata?.(item, ctx) ?? item.metadata ?? null;
  return entry.itemRenderer({ item, query, ctx, entry, metadata });
}

function listEntryKey(entry: EntityFinderListEntry, index: number): string {
  if (entry.type === "header") return `header-${entry.label}-${index}`;
  return entry.item.id;
}

interface EntityFinderListRowProps {
  entry: EntityFinderListEntry;
  index: number;
  query: string;
  ctx: EntityFinderContext;
  onSelect: (item: EntityFinderItem, configType: EntityFinderKind) => void;
  absolute?: boolean;
  style?: React.CSSProperties;
}

function EntityFinderListRow({
  entry,
  index,
  query,
  ctx,
  onSelect,
  absolute = false,
  style,
}: EntityFinderListRowProps) {
  if (entry.type === "header") {
    return (
      <div
        className={cn(absolute && "absolute left-0 top-0 w-full")}
        style={style}
      >
        <EntityFinderSectionHeader title={entry.label} isFirst={index === 0} compact />
      </div>
    );
  }

  if (entry.type === "setting") {
    const { item } = entry;
    return (
      <EntityFinderListItem
        key={item.id}
        value={item.id}
        absolute={absolute}
        onSelect={() => onSelect(item, entry.configType)}
        style={style}
      >
        <EntityFinderSettingRow item={item} query={query} />
      </EntityFinderListItem>
    );
  }

  const { item, configType } = entry;
  return (
    <EntityFinderListItem
      key={item.id}
      value={item.id}
      absolute={absolute}
      onSelect={() => onSelect(item, configType)}
      style={style}
    >
      {renderItemContent(item, configType, query, ctx)}
    </EntityFinderListItem>
  );
}

export function EntityFinderList({
  entries,
  rowVirtualizer,
  query,
  ctx,
  onSelect,
  className,
}: EntityFinderListProps) {
  const virtualItems = rowVirtualizer.getVirtualItems();
  const useFallback = entries.length > 0 && virtualItems.length === 0;

  if (useFallback) {
    return (
      <div className={cn("relative w-full", className)}>
        {entries.map((entry, index) => (
          <EntityFinderListRow
            key={listEntryKey(entry, index)}
            entry={entry}
            index={index}
            query={query}
            ctx={ctx}
            onSelect={onSelect}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className)} style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
      {virtualItems.map((virtualRow) => {
        const entry = entries[virtualRow.index];
        return (
          <EntityFinderListRow
            key={listEntryKey(entry, virtualRow.index)}
            entry={entry}
            index={virtualRow.index}
            query={query}
            ctx={ctx}
            onSelect={onSelect}
            absolute
            style={{
              height: `${virtualRow.size}px`,
              transform: `translateY(${virtualRow.start}px)`,
            }}
          />
        );
      })}
    </div>
  );
}

interface EntityFinderRecentsListProps {
  items: EntityFinderItem[];
  query: string;
  ctx: EntityFinderContext;
  onSelect: (item: EntityFinderItem, configType: EntityFinderKind) => void;
}

export function EntityFinderRecentsList({
  items,
  query,
  ctx,
  onSelect,
}: EntityFinderRecentsListProps) {
  if (items.length === 0) return null;

  return (
    <section className="pb-2">
      <EntityFinderSectionHeader title="Recent" isFirst />
      <div>
        {items.map((item) => (
          <EntityFinderListItem
            key={item.id}
            value={item.id}
            onSelect={() => onSelect(item, item.type)}
          >
            {renderItemContent(item, item.type, query, ctx)}
          </EntityFinderListItem>
        ))}
      </div>
    </section>
  );
}
